import type { User } from '@supabase/supabase-js';
import { supabase, setFailedBackendRequestListener } from '../lib/supabase';

/**
 * Automatic error tickets.
 * Any error a signed-in user hits (error toast, failed database / edge function call,
 * uncaught error, unhandled promise rejection, React crash) becomes a bug_reports row
 * (severity high, status open) and a notification email to NOTIFY_EMAIL.
 * Same error + same user is reported at most once per 10 minutes.
 * The reporter never throws and never reports its own bug_reports / send-email failures.
 */

const NOTIFY_EMAIL = 'info@rapidcert.eu';
const DEDUPE_WINDOW_MS = 10 * 60 * 1000;
const TITLE_MESSAGE_MAX = 120;
const MESSAGE_MAX = 2000;
const BODY_READ_MAX = 4000;
const SESSION_LOOKUP_TIMEOUT_MS = 3000;

const STALE_CHUNK_RELOAD_KEY = 'auto-error-reporter:stale-chunk-reload-at';
const STALE_CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

const recentReports = new Map<string, number>();
/** True once this page has started a stale-chunk reload; later errors from the old page are skipped. */
let staleChunkReloadStarted = false;
let currentUser: User | null = null;
let installed = false;

export interface AutoErrorInput {
  message: string;
  details?: string | null;
  action?: string | null;
}

type BackendErrorBody = {
  message?: unknown;
  error?: unknown;
  details?: unknown;
  hint?: unknown;
  code?: unknown;
};

function clean(text: string, max: number): string {
  const collapsed = String(text ?? '').replace(/\s+/g, ' ').trim();
  return collapsed.length > max ? `${collapsed.slice(0, max - 3)}...` : collapsed;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Page route without query string or hash (those can carry tokens). */
function currentRoute(): string {
  try {
    return typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : 'unknown';
  } catch {
    return 'unknown';
  }
}

function isDuplicateLocally(key: string): boolean {
  const now = Date.now();
  for (const [k, at] of recentReports) {
    if (now - at >= DEDUPE_WINDOW_MS) recentReports.delete(k);
  }
  const last = recentReports.get(key);
  return last !== undefined && now - last < DEDUPE_WINDOW_MS;
}

/** Cross-tab / cross-reload dedupe. Best-effort: if the check itself fails we still report. */
async function isDuplicateInDatabase(userId: string, title: string): Promise<boolean> {
  try {
    const since = new Date(Date.now() - DEDUPE_WINDOW_MS).toISOString();
    const { data, error } = await supabase
      .from('bug_reports')
      .select('id')
      .eq('reporter_id', userId)
      .eq('title', title)
      .gte('created_at', since)
      .limit(1);
    if (error) return false;
    return (data?.length ?? 0) > 0;
  } catch {
    return false;
  }
}

/** Signed-in user, from the cached auth state; falls back to getSession with a timeout. */
async function resolveUser(): Promise<User | null> {
  if (currentUser) return currentUser;
  try {
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), SESSION_LOOKUP_TIMEOUT_MS));
    const lookup = supabase.auth.getSession().then(({ data }) => data.session?.user ?? null);
    return await Promise.race([lookup, timeout]);
  } catch {
    return null;
  }
}

function displayNameOf(user: User): string {
  const meta = (user.user_metadata || {}) as Record<string, unknown>;
  const first = typeof meta.first_name === 'string' ? meta.first_name.trim() : '';
  const last = typeof meta.last_name === 'string' ? meta.last_name.trim() : '';
  const fromParts = [first, last].filter(Boolean).join(' ');
  if (fromParts) return fromParts;
  for (const key of ['full_name', 'name', 'display_name']) {
    const v = meta[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return user.email || user.id;
}

function buildDescription(args: {
  message: string;
  details: string | null;
  action: string | null;
  route: string;
  reporterName: string;
  reporterEmail: string;
}): string {
  const lines = [
    `Message: ${args.message}`,
    args.details ? `Details:\n${args.details}` : null,
    args.action ? `Action: ${args.action}` : null,
    `Page: ${args.route}`,
    `User: ${args.reporterName} <${args.reporterEmail}>`,
    `User agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'}`,
    `Time (UTC): ${new Date().toISOString()}`,
  ];
  return lines.filter((line): line is string => line !== null).join('\n');
}

async function sendNotificationEmail(title: string, description: string): Promise<void> {
  try {
    const html =
      `<p>An error was automatically logged in the Ireland T&amp;T CRM (Bug Reports).</p>` +
      `<pre style="font-family:monospace;white-space:pre-wrap">${escapeHtml(description)}</pre>`;
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        to: [{ email: NOTIFY_EMAIL }],
        subject: title,
        html,
        text: description,
        email_type: 'transactional',
      },
    });
    if (error || (data && (data as { error?: unknown }).error)) {
      console.warn('[auto-error-reporter] notification email failed:', error || (data as { error?: unknown }).error);
    }
  } catch (err) {
    console.warn('[auto-error-reporter] notification email failed:', err);
  }
}

/**
 * After a publish, an open tab still points at the old hashed JS files, which no longer exist.
 * Lazy screens then fail with these messages. That is not a bug: reloading loads the new files.
 */
export function isStaleChunkError(message: string): boolean {
  const text = message || '';
  return (
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading (CSS )?chunk|Unable to preload CSS/i.test(
      text
    ) ||
    // The import of an old screen file came back empty, so `module.SomethingModule` fails
    // (Chrome / Firefox wording, then Safari wording).
    /Cannot read propert(y|ies) of undefined \(reading '[\w$]*Module'\)/i.test(text) ||
    /undefined is not an object \(evaluating '[\w$.]*Module'\)/i.test(text)
  );
}

/** True once this page has started a stale-chunk reload. */
export function isStaleChunkReloadStarted(): boolean {
  return staleChunkReloadStarted;
}

/**
 * Reload the page once for a stale-chunk error. Guarded by sessionStorage so it can never loop:
 * a second stale-chunk error within 5 minutes of the last reload does not reload again.
 * Returns true if a reload was started.
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const last = Number(window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) || 0);
    if (last && Date.now() - last < STALE_CHUNK_RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
    staleChunkReloadStarted = true;
    window.location.reload();
    return true;
  } catch {
    // sessionStorage unavailable (private mode etc.): do not reload, to rule out loops.
    return false;
  }
}

async function submitAutoError(input: AutoErrorInput): Promise<void> {
  try {
    const message = clean(input.message || '', MESSAGE_MAX);
    if (!message) return;

    // The page is already reloading for stale app files: anything else the old page
    // throws on its way out is a side effect of that, not a bug.
    if (staleChunkReloadStarted) return;

    // Stale app files after a publish: reload once instead of creating a ticket.
    // Only if a reload was already tried in the last 5 minutes (so the files are really
    // missing) does it fall through and get reported.
    if (isStaleChunkError(message) && reloadOnceForStaleChunk()) return;

    const user = await resolveUser();
    if (!user?.id) return; // only signed-in users create tickets
    const email = user.email || 'unknown';

    const title = `Auto: ${clean(message, TITLE_MESSAGE_MAX)}`;
    const dedupeKey = `${user.id}|${title}`;
    if (isDuplicateLocally(dedupeKey)) return;
    recentReports.set(dedupeKey, Date.now());

    if (await isDuplicateInDatabase(user.id, title)) return;

    const description = buildDescription({
      message,
      details: input.details ? String(input.details).slice(0, MESSAGE_MAX) : null,
      action: input.action ? clean(input.action, 300) : null,
      route: currentRoute(),
      reporterName: displayNameOf(user),
      reporterEmail: email,
    });

    const { error } = await supabase.from('bug_reports').insert([
      {
        title,
        description,
        severity: 'high',
        status: 'open',
        reporter_id: user.id,
      },
    ]);
    if (error) {
      console.warn('[auto-error-reporter] bug_reports insert failed:', error);
      return;
    }

    await sendNotificationEmail(title, description);
  } catch (err) {
    console.warn('[auto-error-reporter] failed:', err);
  }
}

export function reportAutoError(input: AutoErrorInput): Promise<void> {
  try {
    return submitAutoError(input);
  } catch {
    return Promise.resolve();
  }
}

/** Expected "errors" that are normal app behaviour, not bugs. */
function isExpectedBackendError(status: number, body: BackendErrorBody | null): boolean {
  const code = typeof body?.code === 'string' ? body.code : '';
  const msg = typeof body?.message === 'string' ? body.message : '';
  // .single() / .maybeSingle() with zero rows
  if (status === 406 && code === 'PGRST116') return true;
  // Expired access token; supabase-js refreshes it automatically
  if (status === 401 && (code === 'PGRST301' || /jwt expired/i.test(msg))) return true;
  // Non-super-admin staff being refused super admin data is expected, not a bug
  if (/super admin access required/i.test(msg || (typeof body?.error === 'string' ? body.error : ''))) return true;
  return false;
}

async function readBody(response: Response): Promise<BackendErrorBody | null> {
  try {
    const text = (await response.text()).slice(0, BODY_READ_MAX);
    if (!text) return null;
    try {
      const parsed = JSON.parse(text) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as BackendErrorBody) : { message: String(parsed) };
    } catch {
      return { message: text };
    }
  } catch {
    return null;
  }
}

export async function reportFailedRequest(url: string, method: string, status: number, response: Response): Promise<void> {
  try {
    const body = await readBody(response);
    if (isExpectedBackendError(status, body)) return;

    const endpoint = url.replace(/^https?:\/\/[^/]+/, '');
    const endpointPath = endpoint.split('?')[0];
    const bodyMessage =
      typeof body?.message === 'string' && body.message
        ? body.message
        : typeof body?.error === 'string' && body.error
          ? body.error
          : '';
    const message = bodyMessage || `HTTP ${status} on ${method} ${endpointPath}`;
    const detailLines = [
      `Endpoint: ${method} ${endpoint.slice(0, 500)}`,
      `HTTP status: ${status}`,
      typeof body?.code === 'string' && body.code ? `Error code: ${body.code}` : null,
      typeof body?.hint === 'string' && body.hint ? `Hint: ${body.hint}` : null,
      typeof body?.details === 'string' && body.details ? `Error details: ${body.details}` : null,
    ];
    await reportAutoError({
      message,
      details: detailLines.filter((line): line is string => line !== null).join('\n'),
      action: `${method} ${endpointPath}`,
    });
  } catch {
    // Reporting is best-effort.
  }
}

function errorMessageOf(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && typeof (value as { message?: unknown }).message === 'string') {
    return (value as { message: string }).message;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return 'Unknown error';
  }
}

function stackOf(value: unknown): string | null {
  return value instanceof Error && value.stack ? value.stack : null;
}

/** Browser noise that is not an app bug. */
function isIgnorableRuntimeMessage(message: string): boolean {
  return (
    /ResizeObserver loop/i.test(message) ||
    /super admin access required/i.test(message) ||
    /^Script error\.?$/i.test(message.trim()) ||
    /AbortError|The user aborted a request|signal is aborted/i.test(message)
  );
}

export function installAutoErrorListeners(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  try {
    supabase.auth.onAuthStateChange((_event, session) => {
      currentUser = session?.user ?? null;
    });
  } catch {
    // Falls back to getSession in resolveUser.
  }

  // Vite fires this when a lazy chunk's preload fails (stale files after a publish).
  // It reloads once, but does NOT call event.preventDefault(): with preventDefault Vite makes
  // the failed import resolve to undefined, so `import(...).then((m) => m.ProcessNumbersModule)`
  // crashed with "Cannot read properties of undefined (reading 'ProcessNumbersModule')".
  // Letting the import reject keeps the normal stale-chunk error, which lazyWithReload and
  // the reporter already handle (spinner until the reload, no ticket).
  window.addEventListener('vite:preloadError', () => {
    try {
      if (!staleChunkReloadStarted) reloadOnceForStaleChunk();
    } catch {
      // never throw from the reporter
    }
  });

  setFailedBackendRequestListener(({ url, method, status, response }) => {
    void reportFailedRequest(url, method, status, response);
  });

  window.addEventListener('error', (event) => {
    try {
      const message = event.error ? errorMessageOf(event.error) : event.message;
      if (!message || isIgnorableRuntimeMessage(message)) return;
      void reportAutoError({
        message,
        details: stackOf(event.error),
        action: 'Uncaught error',
      });
    } catch {
      // never throw from the reporter
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const message = errorMessageOf(event.reason);
      if (!message || isIgnorableRuntimeMessage(message)) return;
      void reportAutoError({
        message,
        details: stackOf(event.reason),
        action: 'Unhandled promise rejection',
      });
    } catch {
      // never throw from the reporter
    }
  });
}